import { createSlice } from '@reduxjs/toolkit';
import { activityInitialState, activityReducers } from '../_shared/activityState';

// People slice — state.data[orgID][siteID] = { data, loading }. loadPerson triggers the saga,
// loadPersonStart only flags loading (no saga listens, so no loop), loadPersonSuccess replaces data.

const initialState = {
	loadingAccountDeletionRequest: false,
	activity: { ...activityInitialState },
	// Scoped: data[orgID][siteID] = { data: [], loading: false }
	data: {},
	error: null,
	searchable: true,

	// CRUD loading flags (global — not scoped)
	loadingAddress: false,
	loadingContacts: false,
	loadingBookings: false,
	loadingTeams: false,
	loadingPermissions: false,
	loadingUpdateProfile: false,
	loadingPhotoProfile: false,
	loadingAddProfile: false,
	loadingEnableProfile: false,
	loadingDisableProfile: false,
	loadingDeleteProfile: false,
	loadingResetPassword: false,
	loadingUpdateContact: false,

	// Selected users for invitation flow
	selectedUsers: [],

	// Bulk "Grant Access" (Quick Action) — POST /people/bulk upsert
	manageAccessLoading: false,
	manageAccessResult: null, // { results: [...], propagation: null | { status, credentialIDs, socketEvent } }
	manageAccessError: null,

	// Quick-add a single person (from Manage Access) — POST /people/bulk, returns the new personID so
	// the caller can drop them straight into the access editor.

	// Bulk Add People drawer — POST /people/bulk in chunks; `progress` is null until a run starts.
	bulkAdd: { running: false, cancelRequested: false, progress: null }
};

export const BULK_ADD_EMPTY_PROGRESS = {
	completed: 0,
	total: 0,
	created: 0,
	failed: 0,
	failedRows: [],
	warnings: [],
	aborted: false
};

// Ensures the bucket exists at data[orgID][key]
const ensureBucket = (state, orgID, siteID) => {
	const key = siteID;
	if (!state.data[orgID]) state.data[orgID] = {};
	if (!state.data[orgID][key]) state.data[orgID][key] = { data: [], loading: false };
	return key;
};

// Patches a single person inside the active scope bucket
const patchPerson = (state, orgID, siteID, personID, patcher) => {
	const key = siteID;
	const bucket = state.data[orgID]?.[key];
	if (!bucket?.data) return;
	bucket.data = bucket.data.map((p) => (p.personID === personID ? patcher(p) : p));
};

const peopleSlice = createSlice({
	name: 'people',
	initialState,
	reducers: {
		...activityReducers,
		// ── List load (scoped) ──────────────────────────────────────────

		// Trigger — saga listens (takeLatest). Reducer is a no-op.
		loadPerson: () => {},

		// REFRESH (silent — replaces data without loading flash, used after inline edits)
		// Merges new data while preserving sub-entities (contacts, address, bookings, etc.)
		refreshPeople: () => {},

		// ── Bulk add people ── payload: { rows: [{ firstName, lastName, email }], siteID, userTypeID }
		bulkAddPeople(state, action) {
			state.bulkAdd = {
				running: true,
				cancelRequested: false,
				progress: { ...BULK_ADD_EMPTY_PROGRESS, total: action.payload?.rows?.length || 0 }
			};
		},
		bulkAddPeopleProgress(state, action) {
			state.bulkAdd.progress = action.payload;
		},
		bulkAddPeopleDone(state, action) {
			state.bulkAdd = { running: false, cancelRequested: false, progress: action.payload };
		},
		// Rows already sent stay created; the saga stops before the next chunk.
		cancelBulkAddPeople(state) {
			if (state.bulkAdd.running) state.bulkAdd.cancelRequested = true;
		},
		resetBulkAddPeople(state) {
			if (!state.bulkAdd.running) state.bulkAdd = initialState.bulkAdd;
		},
		refreshPeopleSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			const existing = state.data[orgID][key].data || [];

			// Build lookup of existing sub-entities by personID
			const subEntityMap = {};
			existing.forEach((p) => {
				if (p.contacts || p.address || p.bookings || p.teams || p.permissions) {
					subEntityMap[p.personID] = {};
					if (p.contacts) subEntityMap[p.personID].contacts = p.contacts;
					if (p.address) subEntityMap[p.personID].address = p.address;
					if (p.bookings) subEntityMap[p.personID].bookings = p.bookings;
					if (p.teams) subEntityMap[p.personID].teams = p.teams;
					if (p.permissions) subEntityMap[p.personID].permissions = p.permissions;
				}
			});

			// Merge: new data + preserved sub-entities
			state.data[orgID][key].data = data.map((p) => {
				const preserved = subEntityMap[p.personID];
				return preserved ? { ...p, ...preserved } : p;
			});
		},

		// Sets loading on the target bucket. No saga listens (prevents loop).
		loadPersonStart(state, action) {
			const { orgID, siteID } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key].loading = true;
			state.error = null;
		},

		// Replaces bucket data — backend is source of truth for this scope.
		loadPersonSuccess(state, action) {
			const { orgID, siteID, data } = action.payload;
			const key = ensureBucket(state, orgID, siteID);
			state.data[orgID][key] = { data, loading: false, loaded: true };
		},

		// Stops loading on the bucket, sets global error.
		loadPersonError(state, action) {
			const { orgID, siteID, error } = action.payload;
			const key = siteID;
			if (state.data[orgID]?.[key]) state.data[orgID][key].loading = false;
			state.error = error;
		},

		// ── Sub-entity patchers (contacts, address, bookings, etc.) ─────
		// Patch one person in the active scope bucket; sagas pass { orgID, siteID, personID, data }.

		// Load person contacts
		loadPersonContact(state) {
			state.loadingContacts = true;
			state.error = null;
		},
		loadPersonContactSuccess(state, action) {
			const { orgID, siteID, personID, data } = action.payload;
			patchPerson(state, orgID, siteID, personID, (p) => ({
				...p,
				contacts: { data, timeStamp: Date.now() }
			}));
			state.loadingContacts = false;
		},
		loadPersonContactError(state, action) {
			state.loadingContacts = false;
			state.error = action.payload;
		},

		// Add person contact
		addPersonContact(state) {
			state.loadingContacts = true;
			state.error = null;
		},
		addPersonContactSuccess(state) {
			state.loadingContacts = false;
			state.error = null;
		},
		addPersonContactError(state, action) {
			state.loadingContacts = false;
			state.error = action.payload;
		},

		// Ask support to delete the signed-in person's account (an email; nothing is removed here).
		requestAccountDeletion(state) {
			state.loadingAccountDeletionRequest = true;
		},
		requestAccountDeletionSuccess(state) {
			state.loadingAccountDeletionRequest = false;
		},
		requestAccountDeletionError(state, action) {
			state.loadingAccountDeletionRequest = false;
			state.error = action.payload;
		},

		// Update person contact
		updatePersonContact(state) {
			state.loadingUpdateContact = true;
			state.error = null;
		},
		updatePersonContactSuccess(state) {
			state.loadingUpdateContact = false;
			state.error = null;
		},
		updatePersonContactError(state, action) {
			state.loadingUpdateContact = false;
			state.error = action.payload;
		},

		// Load person address
		loadPersonAddress(state) {
			state.loadingAddress = true;
			state.error = null;
		},
		loadPersonAddressSuccess(state, action) {
			const { orgID, siteID, personID, data } = action.payload;
			patchPerson(state, orgID, siteID, personID, (p) => ({
				...p,
				address: { data, timeStamp: Date.now() }
			}));
			state.loadingAddress = false;
		},
		loadPersonAddressError(state, action) {
			state.loadingAddress = false;
			state.error = action.payload;
		},

		// Load person bookings
		loadPersonBooking(state) {
			state.loadingBookings = true;
			state.error = null;
		},
		loadPersonBookingSuccess(state, action) {
			const { orgID, siteID, personID, data } = action.payload;
			patchPerson(state, orgID, siteID, personID, (p) => ({
				...p,
				bookings: { data, timeStamp: Date.now() }
			}));
			state.loadingBookings = false;
		},
		loadPersonBookingError(state, action) {
			state.loadingBookings = false;
			state.error = action.payload;
		},

		// Load person teams
		loadPersonTeams(state) {
			state.loadingTeams = true;
			state.error = null;
		},
		loadPersonTeamsSuccess(state, action) {
			const { orgID, siteID, personID, data } = action.payload;
			patchPerson(state, orgID, siteID, personID, (p) => ({
				...p,
				teams: { data, timeStamp: Date.now() }
			}));
			state.loadingTeams = false;
		},
		loadPersonTeamsError(state, action) {
			state.loadingTeams = false;
			state.error = action.payload;
		},

		// Load person permissions
		loadPersonPermissions(state) {
			state.loadingPermissions = true;
			state.error = null;
		},
		loadPersonPermissionsSuccess(state, action) {
			const { orgID, siteID, personID, data } = action.payload;
			patchPerson(state, orgID, siteID, personID, (p) => ({
				...p,
				permissions: { data, timeStamp: Date.now() }
			}));
			state.loadingPermissions = false;
		},
		loadPersonPermissionsError(state, action) {
			state.loadingPermissions = false;
			state.error = action.payload;
		},

		// ── CRUD actions (global loading flags) ─────────────────────────

		// Create staged Zezamii users (POST /people/bulk)
		createPeople(state) {
			state.loadingAddProfile = true;
			state.error = null;
		},
		createPeopleSuccess(state) {
			state.loadingAddProfile = false;
			state.error = null;
		},
		createPeopleError(state, action) {
			state.loadingAddProfile = false;
			state.error = action.payload;
		},

		// Bulk grant access (Quick Action → POST /people/bulk upsert)
		manageAccessBulk(state) {
			state.manageAccessLoading = true;
			state.manageAccessResult = null;
			state.manageAccessError = null;
		},
		manageAccessBulkSuccess(state, action) {
			state.manageAccessLoading = false;
			state.manageAccessResult = action.payload; // { results, propagation }
			state.manageAccessError = null;
		},
		manageAccessBulkError(state, action) {
			state.manageAccessLoading = false;
			state.manageAccessError = action.payload;
		},
		manageAccessBulkReset(state) {
			state.manageAccessLoading = false;
			state.manageAccessResult = null;
			state.manageAccessError = null;
		},

		// Create pending visitors (no Zezamii login)
		createVisitors(state) {
			state.loadingAddProfile = true;
			state.error = null;
		},
		createVisitorsSuccess(state) {
			state.loadingAddProfile = false;
			state.error = null;
		},
		createVisitorsError(state, action) {
			state.loadingAddProfile = false;
			state.error = action.payload;
		},

		// Update person profile
		updatePerson(state) {
			state.loadingUpdateProfile = true;
			state.error = null;
		},
		updatePersonSuccess(state, action) {
			state.loadingUpdateProfile = false;
			state.error = null;
		},
		updatePersonError(state, action) {
			state.loadingUpdateProfile = false;
			state.error = action.payload;
		},

		// Update person sites
		updatePersonSites: (state, action) => {
			state.loadingUpdateSites = true;
			state.error = null;
			// Optimistic update: patch person.sites in all buckets immediately
			const { personID, siteIDs } = action.payload || {};
			if (personID && siteIDs) {
				const newSites = siteIDs.map((id) => ({ siteID: id }));
				Object.keys(state.data).forEach((orgID) => {
					Object.keys(state.data[orgID]).forEach((siteKey) => {
						const bucket = state.data[orgID][siteKey];
						if (bucket?.data) {
							bucket.data = bucket.data.map((p) =>
								p.personID === personID ? { ...p, sites: newSites } : p
							);
						}
					});
				});
			}
		},
		updatePersonSitesSuccess: (state) => {
			state.loadingUpdateSites = false;
		},
		updatePersonSitesError: (state, action) => {
			state.loadingUpdateSites = false;
			state.error = action.payload;
		},

		// Enable person — the modal closes optimistically on dispatch; this flag only
		// drives the topbar in-flight indicator, so no success flag is needed.
		enablePerson(state) {
			state.loadingEnableProfile = true;
			state.error = null;
		},
		enablePersonSuccess(state, action) {
			state.loadingEnableProfile = false;
			state.error = null;
		},
		enablePersonError(state, action) {
			state.loadingEnableProfile = false;
			state.error = action.payload;
		},

		// Disable person — closes optimistically; flag only drives the topbar indicator.
		disablePerson(state) {
			state.loadingDisableProfile = true;
			state.error = null;
		},
		disablePersonSuccess(state, action) {
			state.loadingDisableProfile = false;
			state.error = null;
		},
		disablePersonError(state, action) {
			state.loadingDisableProfile = false;
			state.error = action.payload;
		},

		// Reset person password
		resetPersonPassword(state) {
			state.loadingResetPassword = true;
			state.error = null;
		},
		resetPersonPasswordSuccess(state) {
			state.loadingResetPassword = false;
			state.error = null;
		},
		resetPersonPasswordError(state, action) {
			state.loadingResetPassword = false;
			state.error = action.payload;
		},

		// Admin-issued reset: the server mints a temporary password in Kinde and emails it. Shares
		// `loadingResetPassword` so the buttons that already bind to it keep their spinner.
		resetPersonPasswordByAdmin(state) {
			state.loadingResetPassword = true;
			state.error = null;
		},
		resetPersonPasswordByAdminSuccess(state) {
			state.loadingResetPassword = false;
			state.error = null;
		},
		resetPersonPasswordByAdminError(state, action) {
			state.loadingResetPassword = false;
			state.error = action.payload;
		},

		// Delete person — closes optimistically; flag only drives the topbar indicator.
		deletePerson(state) {
			state.loadingDeleteProfile = true;
			state.error = null;
		},
		deletePersonSuccess(state) {
			state.loadingDeleteProfile = false;
			state.error = null;
		},
		deletePersonError(state, action) {
			state.loadingDeleteProfile = false;
			state.error = action.payload;
		},

		// ── Selected users for invitation ───────────────────────────────

		addSelectedUser(state, action) {
			const newUser = action.payload;
			state.selectedUsers.push(newUser);
		},
		removeSelectedUser(state, action) {
			const { personID } = action.payload;
			state.selectedUsers = state.selectedUsers.filter((user) => user.personID !== personID);
		},
		updateSelectedUserStatus(state, action) {
			const { status, personID, newPersonID } = action.payload;

			state.selectedUsers = state.selectedUsers.map((user) =>
				user.personID === personID
					? {
							...user,
							status,
							personID: newPersonID || user.personID
						}
					: user
			);
		},
		clearSelectedUsers(state) {
			state.selectedUsers = [];
		}
	},
	extraReducers: (builder) => {
		builder.addCase('auth/logout', () => {
			return initialState;
		});
	}
});

export const peopleActions = peopleSlice.actions;

export default peopleSlice.reducer;
